import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Spinner,
  Switch,
} from '@eureca/ui';
import { XIcon } from '@eureca/ui/icons';

/**
 * EU-641 — painel lateral "FAQs padrão" dos 4 fluxos (criar/editar vaga e
 * programa). Título padronizado (decisão do card sobre o Figma "FAQs
 * padrões"), fechar no topo, SEM rodapé/salvar (FQC-005). Cada item:
 * pergunta + switch de visibilidade + resposta expansível (expandir nunca
 * mexe no switch — FQC-006/016). Texto somente leitura (FQC-007).
 * `disabled` (registro Encerrado/Cancelado — EU-569/570) mantém leitura e
 * expansão liberadas com switches desabilitados (FQC-023).
 */

export interface DefaultFaqsPanelItem {
  id: number;
  question: string;
  answer: string;
  hidden: boolean;
}

export interface DefaultFaqsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: DefaultFaqsPanelItem[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** Registro terminal: switches desabilitados, leitura liberada (FQC-023). */
  disabled?: boolean;
  onToggle: (faqId: number, hidden: boolean) => void;
}

export function DefaultFaqsPanel({
  open,
  onOpenChange,
  items,
  loading = false,
  error = false,
  onRetry,
  disabled = false,
  onToggle,
}: DefaultFaqsPanelProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          'gap-0 p-0',
          'lg:left-auto lg:right-0 lg:top-0 lg:bottom-0',
          'lg:h-dvh lg:max-h-dvh lg:w-full lg:max-w-[662px]',
          'lg:translate-x-0 lg:translate-y-0 lg:rounded-none',
          'flex flex-col'
        )}
      >
        <header className="flex flex-row items-center gap-2 border-b border-border px-6 py-6">
          <DialogTitle className="flex-1 text-2xl font-medium leading-none tracking-tight text-accent-foreground">
            FAQs padrão
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Fechar"
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid="default-faqs-panel-close"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-2">
          {loading && (
            <div
              className="flex flex-row items-center gap-2 py-8 text-sm text-muted-foreground"
              data-testid="default-faqs-panel-loading"
            >
              <Spinner className="size-4" />
              Carregando perguntas…
            </div>
          )}

          {!loading && error && (
            <div
              className="flex flex-col items-start gap-3 py-8"
              data-testid="default-faqs-panel-error"
            >
              <p className="text-sm text-muted-foreground">
                Não foi possível carregar as perguntas padrão.
              </p>
              <button
                type="button"
                onClick={onRetry}
                className="font-medium text-primary underline-offset-2 hover:underline"
                data-testid="default-faqs-panel-retry"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && !error && (
            <Accordion type="multiple" className="border-t-0">
              {items.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={String(faq.id)}
                  className="border-b border-border"
                >
                  <div className="flex flex-row items-center gap-3">
                    <AccordionTrigger className="flex-1 py-4 text-left text-base font-medium leading-6 text-foreground hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <Switch
                      checked={!faq.hidden}
                      disabled={disabled}
                      onCheckedChange={(visible) => onToggle(faq.id, !visible)}
                      aria-label={`Exibir "${faq.question}"`}
                      data-testid={`default-faq-switch-${faq.id}`}
                      className={cn(disabled && 'opacity-50')}
                    />
                  </div>
                  <AccordionContent className="text-sm leading-5 text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
